"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { m as motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FooterHypercube } from "@/components/layout/FooterHypercube";

// not-found globale: reso fuori dal segmento [locale], quindi la lingua non
// arriva da next-intl. La deduco dal path tentato (/en/... -> inglese).
// Voce in prima persona = tono di Dario, leggermente sulla battuta.
const COPY = {
  it: {
    eyebrow: "Errore 404",
    title: (
      <>
        Questa pagina è in un&apos;altra{" "}
        <span className="text-primary">dimensione</span>.
      </>
    ),
    sub: (
      <>
        Nemmeno io la trovo, e ho quattro dimensioni in cui cercare.
        <br />
        Dev&apos;essere finita in una piega dello spaziotempo.
      </>
    ),
    cta: "Torna alla home",
    home: "/",
    logoAlt: "Dimensione 4",
  },
  en: {
    eyebrow: "Error 404",
    title: (
      <>
        This page is in another{" "}
        <span className="text-primary">dimension</span>.
      </>
    ),
    sub: (
      <>
        Even I can&apos;t find it, and I&apos;ve got four dimensions to search.
        <br />
        It must have slipped into a fold in spacetime.
      </>
    ),
    cta: "Back to home",
    home: "/en",
    logoAlt: "Dimensione 4",
  },
};

export default function NotFound() {
  const pathname = usePathname();
  const t = pathname?.startsWith("/en") ? COPY.en : COPY.it;

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground md:h-screen">
      {/* Atmosfera: dot grid + alone ciano centrale */}
      <div className="pointer-events-none absolute inset-0 pattern-dots opacity-50" aria-hidden="true" />
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[120px]"
        aria-hidden="true"
      />

      {/* Logo: via di ritorno sempre visibile */}
      <div className="relative z-10 p-5 md:p-6">
        <Link href={t.home} className="inline-flex items-center" aria-label={t.logoAlt}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/brand/logo-no-payoff.png"
            alt={t.logoAlt}
            width={1024}
            height={126}
            className="h-6 w-auto object-contain md:h-7"
          />
        </Link>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-12 text-center md:pb-0">
        {/* Signature: il tesseract (ipercubo 4D = Dimensione 4) sospeso sopra
            un "404" fantasma gigante = il vuoto in cui sei caduto. */}
        <motion.div
          className="relative flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute select-none font-display font-bold leading-none tracking-tighter text-foreground/[0.05] text-[38vw] md:text-[20rem]"
          >
            404
          </span>
          <FooterHypercube />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.24em] text-primary">
            {t.eyebrow}
          </p>
          <h1 className="mx-auto mt-3 max-w-2xl font-display text-3xl font-bold leading-[1.5] md:text-5xl md:leading-[1.2]" >
            {t.title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground md:text-lg">
            {t.sub}
          </p>

          <Button variant="outline" size="lg" className="rotating-border mt-7" asChild>
            <Link href={t.home}>
              <ArrowLeft className="h-4 w-4" />
              {t.cta}
            </Link>
          </Button>
        </motion.div>
      </div>
    </main>
  );
}
